import { type FormikProps } from 'formik';
import { useMemo } from 'react';

import {
  type FieldSuggestion,
  type ParentFormikFormValues,
  type UpdateSuggestionAction,
} from 'hb-react/shared/components/AIFormWidget/types';

interface UseSuggestionProps {
  formik: FormikProps<ParentFormikFormValues>;
  suggestion: FieldSuggestion;
  updateSuggestion: UpdateSuggestionAction;
}

export const useSuggestion = ({
  suggestion: initialSuggestion,
  updateSuggestion,
  formik,
}: UseSuggestionProps) => {
  const suggestion = useMemo(() => {
    if (!initialSuggestion.history) {
      return {
        ...initialSuggestion,
        history: [
          {
            id: `original-${initialSuggestion.id}`,
            suggestedValue: initialSuggestion.suggestedValue,
            reasoning: initialSuggestion.reasoning,
            timestamp: Date.now(),
          },
        ],
        currentHistoryIndex: 0,
      };
    }

    return initialSuggestion;
  }, [initialSuggestion]);

  const navigateHistory = (index: number) => {
    const history = suggestion.history || [];

    if (index < 0 || index >= history.length) {
      return;
    }

    const historyEntry = history[index];

    updateSuggestion({
      id: suggestion.id,
      suggestedValue: historyEntry.suggestedValue,
      reasoning: historyEntry.reasoning,
      currentHistoryIndex: index,
    });
  };

  const handleUndo = () => {
    formik.setFieldValue(suggestion.fieldName, suggestion.originalValue);

    updateSuggestion({
      id: suggestion.id,
      status: 'pending',
    });
  };

  const handleUndoRejection = () => {
    updateSuggestion({
      id: suggestion.id,
      status: 'pending',
    });
  };

  return {
    suggestion,
    navigateHistory,
    handleUndo,
    handleUndoRejection,
  };
};
